import React, { createContext, useContext, useState, useMemo, ReactNode, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppContext } from 'views/App/context';
import { useField } from './_data_sources/useField';
import useManageFields from './_data_sources/useManageFields';
import { Field } from './_types/fieldbank';

interface FieldBankContextValue {
  isLoading: boolean;
  isError: boolean;
  error: any;
  fieldData: Field | null;
  setFieldData: React.Dispatch<React.SetStateAction<Field | null>>;
  updateField: (updatedField: Field, changeDescription?: string) => Promise<void>;
}

export const FieldBankContext = createContext<FieldBankContextValue>({} as FieldBankContextValue);

export const FieldBankProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { id } = useParams();
  const { currentOrganizationId: orgId } = useContext(AppContext);
  const navigate = useNavigate();
  const fieldQuery = useField(Number(id), true);
  const { updateField } = useManageFields();
  const [fieldData, setFieldData] = useState<Field | null>(null);

  useEffect(() => {
    if (fieldQuery.data) {
      setFieldData(fieldQuery.data);
    }
  }, [fieldQuery.data]);

  const handleUpdateField = async (updatedField: Field, changeDescription?: string) => {
    if (!orgId || !id) return;

    return new Promise<void>((resolve, reject) => {
      updateField(
        updatedField,
        changeDescription,
        (response) => {
          // Success callback
          setFieldData(updatedField);
          resolve();
        },
        (error) => {
          // Error callback
          console.error('Error updating field:', error);
          reject(error);
        }
      );
    });
  };

  const value: FieldBankContextValue = useMemo(
    () => ({
      isLoading: fieldQuery.isLoading,
      isError: fieldQuery.isError,
      error: fieldQuery.error,
      fieldData,
      setFieldData,
      updateField: handleUpdateField,
    }),
    [fieldQuery, fieldData]
  );

  return <FieldBankContext.Provider value={value}>{children}</FieldBankContext.Provider>;
};

export const useFieldBankContext = () => useContext(FieldBankContext);
